/**
 * CallLeadPopupContext — global gate for the reusable "call a lead by phone"
 * popup. Any UI surface across the app (sidebar lead-info, lead profile,
 * deal card, leads list, etc.) can trigger an outbound phone call by
 * importing `useCallLeadPopup()` and invoking `openCall({...})`. The popup
 * itself is rendered as a sibling of the children so every consumer sees
 * the same modal — never multiple instances at once.
 *
 * Call lifecycle vs. WS impact:
 *   The popup mints a lead-scoped Voice token (`POST /voice/token` with the
 *   lead's id), so the backend creates a call session and the call IS
 *   session-linked — `agent:engaging` / `agent:disengaged` events flow to the
 *   lead's portal just like the dashboard CALL LEAD route. A numeric lead
 *   `id` is therefore REQUIRED to open the popup. See twilio.context.tsx's
 *   connect().
 */
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTwilioState } from "@/context/twilio.context";
import CallLeadPopup from "@/components/call-lead/CallLeadPopup";
import ActiveCallIndicator from "@/components/call-lead/ActiveCallIndicator";
import CallRefreshGuard from "@/components/call-lead/CallRefreshGuard";

/**
 * Minimum lead identity needed to start a call.
 *
 * `id` is required: the Voice token is lead-scoped, so without a numeric lead
 * id there's no token and no call. `phoneNumber` is required too — a "Call"
 * CTA without a number to dial is a bug at the call site. Other fields are
 * display-only and degrade gracefully when absent.
 */
export type CallableLead = {
  id: number;
  name: string;
  /** Display label under the name, e.g. job title. Optional. */
  role?: string;
  /** Display label after the role (e.g. "Career Changer · Acme Holdings"). Optional. */
  company?: string;
  /** E.164 — what Twilio actually dials. */
  phoneNumber: string;
};

type CallLeadPopupContextValue = {
  isOpen: boolean;
  currentLead: CallableLead | null;
  /**
   * True while the popup call is running in the background: the modal is
   * hidden (but stays MOUNTED — see the render note below) and the floating
   * ActiveCallIndicator represents the call instead, so the agent can
   * navigate the app freely mid-call.
   */
  isMinimized: boolean;
  openCall: (lead: CallableLead) => void;
  closeCall: () => void;
  /** Hide the popup and hand the call over to the floating indicator. */
  minimizeCall: () => void;
  /** Bring the full popup back from the floating indicator. */
  maximizeCall: () => void;
};

const CallLeadPopupContext = createContext<CallLeadPopupContextValue | null>(null);

export const CallLeadPopupProvider = ({ children }: PropsWithChildren) => {
  const [currentLead, setCurrentLead] = useState<CallableLead | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);

  // Ref mirror so openCall can read the live call state without depending on
  // it — keeping openCall (and therefore the context value) referentially
  // stable across call-state transitions.
  const { state, isCallActive } = useTwilioState();
  const isCallActiveRef = useRef(isCallActive);
  isCallActiveRef.current = isCallActive;

  const openCall = useCallback((lead: CallableLead) => {
    // Never (re)open over a LIVE call: the Twilio state is global, so the
    // popup would relabel the in-flight call with this lead's identity —
    // name and phone of B over a live call to A, "End call" hanging up A.
    // Callers already guard with isCallActive (CallLeadButton disables), so
    // this is the provider enforcing its own contract against future
    // surfaces that don't.
    if (isCallActiveRef.current) return;
    setIsMinimized(false);
    setCurrentLead(lead);
  }, []);

  const closeCall = useCallback(() => {
    setCurrentLead(null);
    setIsMinimized(false);
  }, []);

  const minimizeCall = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const maximizeCall = useCallback(() => {
    setIsMinimized(false);
  }, []);

  // A call that reaches a terminal state while minimized re-expands the popup
  // automatically: the terminal view (why it ended + the explicit Close that
  // releases the call slot) is the popup's, and a minimized bar silently
  // vanishing would leave the agent wondering what happened to their call.
  useEffect(() => {
    if (!currentLead || !isMinimized) return;
    if (state === "ended" || state === "error") {
      setIsMinimized(false);
    }
  }, [state, currentLead, isMinimized]);

  const value = useMemo<CallLeadPopupContextValue>(
    () => ({
      isOpen: currentLead !== null,
      currentLead,
      isMinimized,
      openCall,
      closeCall,
      minimizeCall,
      maximizeCall,
    }),
    [currentLead, isMinimized, openCall, closeCall, minimizeCall, maximizeCall],
  );

  return (
    <CallLeadPopupContext.Provider value={value}>
      {children}
      {/* Global call surfaces — all rendered as siblings of children so
          they overlay any route. The indicator self-gates on call state +
          popup visibility + current pathname; the refresh guard self-gates
          on call activity. The popup only renders when explicitly opened. */}
      <ActiveCallIndicator />
      <CallRefreshGuard />
      {/* key: a DIFFERENT lead remounts the popup so its once-per-mount
          auto-dial fires for the new lead — replacing the prop on the live
          instance would leave the new lead stuck on the old call's terminal
          state.

          minimized is a PROP (the popup renders null), never an unmount:
          unmounting would reset the popup's once-per-mount auto-dial guard,
          and maximizing after the call ended would re-dial the lead. */}
      {currentLead && (
        <CallLeadPopup
          key={currentLead.id}
          lead={currentLead}
          onClose={closeCall}
          minimized={isMinimized}
          onMinimize={minimizeCall}
        />
      )}
    </CallLeadPopupContext.Provider>
  );
};

export const useCallLeadPopup = (): CallLeadPopupContextValue => {
  const ctx = useContext(CallLeadPopupContext);
  if (!ctx) {
    throw new Error("useCallLeadPopup must be used inside CallLeadPopupProvider");
  }
  return ctx;
};
